import React from 'react';
import type { UiMessage, LocalFilePreview } from './conversation.hooks';
import { formatMessageDateTime, getFileMeta } from './conversation.hooks';
import {
  Wrap,
  ListWrap,
  List,
  Row,
  MineConversationWrapper,
  MineText,
  AdminContainer,
  AdminText,
  DateRow,
  AdminFooter,
  SentViaSmsLabel,
  DateAndLabelRow,
  AvatarCol,
  ContentCol,
  LocalPreviewWrap,
  LocalPreviewImg,
  PreviewOverlay,
  CancelUploadBtn,
  FileUploadProgressTrack,
  FileUploadProgressFill,
  UploadProgressPill,
  RetryTap,
  SentCheck,
  TranscribingIndicator,
  FileCard,
  FileTextCol,
  FileName,
  FileMeta,
} from './conversation.styled';
import { FileAttachment } from './file-attachment';
import { useAuth } from '../../../contexts/auth/auth-context';
import { UniversalSpinner } from '../../../components/universal-spinner/universal-spinner';
import { AppAvatar } from '../../../components/avatar/avatar';
import { ConversationListenButton } from './listen-button/listen-button';

import { api } from '../../../api/api';
import { linkify } from '../../../utils/linkify';

/* ── Circular SVG Progress (image thumbnail overlay) ── */
const CircularProgress: React.FC<{ progress: number; size?: number }> = ({ progress, size = 36 }) => {
  const r = (size / 2) - 3;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (progress / 100) * circumference;
  return (
    <svg width={size} height={size} style={{ display: 'block' }}>
      <circle cx={size / 2} cy={size / 2} r={r}
        fill="none" stroke="#2b2d33" strokeWidth={2.5} />
      <circle cx={size / 2} cy={size / 2} r={r}
        fill="none" stroke="#5e84ed" strokeWidth={2.5}
        strokeDasharray={circumference} strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset 0.2s ease-out' }} />
    </svg>
  );
};

/* ── Local file preview card (shown during upload before server responds) ── */
const LocalFilePreviewCard: React.FC<{
  file: LocalFilePreview;
  progress: number | null;
  onCancel?: () => void;
}> = ({ file, progress, onCancel }) => {
  const isImage = file.type.startsWith('image/') && !!file.previewUrl;
  const showProgress = progress != null;
  const { typeLabel, sizeLabel } = getFileMeta({ name: file.name, file_type: file.type, file_size: file.size });

  if (isImage) {
    return (
      <LocalPreviewWrap>
        <LocalPreviewImg src={file.previewUrl!} alt={file.name} />
        {showProgress && (
          <>
            <PreviewOverlay>
              <CircularProgress progress={progress} />
            </PreviewOverlay>
            {onCancel && (
              <CancelUploadBtn onClick={onCancel} aria-label="Cancel upload">
                ✕
              </CancelUploadBtn>
            )}
          </>
        )}
      </LocalPreviewWrap>
    );
  }

  // Non-image file card
  return (
    <FileCard>
      <FileTextCol>
        <FileName>{file.name}</FileName>
        <FileMeta>{typeLabel} · {sizeLabel}</FileMeta>
        {showProgress && (
          <FileUploadProgressTrack>
            <FileUploadProgressFill progress={progress} />
          </FileUploadProgressTrack>
        )}
      </FileTextCol>
      {showProgress && onCancel && (
        <CancelUploadBtn
          onClick={onCancel}
          aria-label="Cancel upload"
          style={{ position: 'relative', top: 'auto', right: 'auto', flexShrink: 0 }}
        >
          ✕
        </CancelUploadBtn>
      )}
    </FileCard>
  );
};

import { decodeDurationMs } from '../../../helpers/decode-duration';

type AudioCacheEntry = {
  url: string;
  createdAt: number;
  revokeOnCleanup: boolean;
};

export const Conversation: React.FC<{
  messages: UiMessage[];
  isLoading: boolean;
  wsConnected: boolean;
  loadOlderMessages: () => Promise<void>;
  hasMore: boolean;
  isLoadingOlder: boolean;
  /** When set, auto-play audio for this message after mount/render */
  autoPlayMessageId?: string | null;
  /** Called after auto-play has been handled (so parent can clear the prop) */
  onAutoPlayHandled?: () => void;
  /** When set, scroll to this message (by server ID / ui_id) */
  scrollToMessageId?: string | null;
  /** Called after scroll-to-message has been handled */
  onScrollHandled?: () => void;
  /** Retry a failed message */
  retryMessage?: (uiId: string) => void;
  /** Cancel an in-progress upload */
  cancelUpload?: (uiId: string) => void;
  /** Single source of truth: scroll to the bottom of ChatWrapper */
  scrollToBottom?: () => void;
  /** Cancel all pending scroll-to-bottom calls (timers + rAF) */
  cancelPendingScrollToBottom?: () => void;
  /** Re-enable scroll-to-bottom after it was suppressed */
  unsuppressScrollToBottom?: () => void;
  /** Load newer messages (forward pagination for "view in conversation") */
  loadNewerMessages?: () => Promise<void>;
  /** Whether there are more newer messages to load */
  hasMoreNewer?: boolean;
  /** Whether newer messages are currently loading */
  isLoadingNewer?: boolean;
  /** Search query to highlight within the target message */
  searchHighlightQuery?: string | null;
}> = ({ messages, isLoading, loadOlderMessages, hasMore, isLoadingOlder, autoPlayMessageId, onAutoPlayHandled, scrollToMessageId, onScrollHandled, retryMessage, cancelUpload, scrollToBottom, cancelPendingScrollToBottom, unsuppressScrollToBottom, loadNewerMessages, hasMoreNewer, isLoadingNewer, searchHighlightQuery }) => {
  const { profile } = useAuth();
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);

  // Scroll preservation using anchor-based approach:
  // Before loading older messages, we save a reference to the first message element
  // and its position on screen. After prepend, we scroll so that element is back
  // at the same screen position. Works regardless of which container scrolls.
  const anchorElRef = React.useRef<Element | null>(null);
  const anchorTopRef = React.useRef(0);
  const prevMessageCountRef = React.useRef(messages.length);
  // Flag to suppress auto-scroll-to-bottom while browsing history
  const suppressAutoScrollRef = React.useRef(false);
  // Flag to prevent observer double-firing while a load is in progress
  const loadInProgressRef = React.useRef(false);

  // True while scroll-to-message is active — guards observer callbacks
  // and auto-scroll effects from interfering with positioning.
  const scrollToActiveRef = React.useRef(!!scrollToMessageId);

  // Refs for spinner scroll compensation — when the spinner disappears
  // we adjust scrollTop so the conversation doesn't jump.
  const olderSpinnerRef = React.useRef<HTMLDivElement | null>(null);
  const olderSpinnerHeightRef = React.useRef(0);
  const newerSpinnerRef = React.useRef<HTMLDivElement | null>(null);
  const newerSpinnerHeightRef = React.useRef(0);
  const prevIsLoadingOlderRef = React.useRef(isLoadingOlder);
  const prevIsLoadingNewerRef = React.useRef(isLoadingNewer);

  // Bottom sentinel for forward pagination ("view in conversation")
  const bottomSentinelRef = React.useRef<HTMLDivElement | null>(null);
  const loadNewerInProgressRef = React.useRef(false);
  const bottomAnchorElRef = React.useRef<Element | null>(null);
  const bottomAnchorTopRef = React.useRef(0);

  // Stable refs so the observer callback always reads fresh values
  // without needing to recreate the observer on every state change.
  const loadOlderRef = React.useRef(loadOlderMessages);
  const hasMoreRef = React.useRef(hasMore);
  const isLoadingOlderRef = React.useRef(isLoadingOlder);
  const loadNewerRef = React.useRef(loadNewerMessages);
  const hasMoreNewerRef = React.useRef(hasMoreNewer);
  const isLoadingNewerRef = React.useRef(isLoadingNewer);
  React.useLayoutEffect(() => {
    loadOlderRef.current = loadOlderMessages;
    hasMoreRef.current = hasMore;
    isLoadingOlderRef.current = isLoadingOlder;
    loadNewerRef.current = loadNewerMessages;
    hasMoreNewerRef.current = hasMoreNewer ?? false;
    isLoadingNewerRef.current = isLoadingNewer ?? false;
  });

  // When the older-messages spinner appears, let it show naturally (no scroll compensation).
  // When it disappears, compensate scrollTop so messages don't jump up.
  React.useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) { prevIsLoadingOlderRef.current = isLoadingOlder; return; }

    // Spinner just APPEARED — measure and store its height for later compensation.
    if (!prevIsLoadingOlderRef.current && isLoadingOlder && olderSpinnerRef.current) {
      olderSpinnerHeightRef.current = olderSpinnerRef.current.getBoundingClientRect().height;
    }

    // Spinner just DISAPPEARED — content shifted up. Subtract saved height so messages stay still.
    if (prevIsLoadingOlderRef.current && !isLoadingOlder && olderSpinnerHeightRef.current > 0) {
      list.scrollTop = Math.max(0, list.scrollTop - olderSpinnerHeightRef.current);
      olderSpinnerHeightRef.current = 0;
    }

    prevIsLoadingOlderRef.current = isLoadingOlder;
  }, [isLoadingOlder]);

  // Same for newer-messages spinner
  React.useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) { prevIsLoadingNewerRef.current = isLoadingNewer; return; }

    if (!prevIsLoadingNewerRef.current && isLoadingNewer && newerSpinnerRef.current) {
      newerSpinnerHeightRef.current = newerSpinnerRef.current.getBoundingClientRect().height;
    }

    if (prevIsLoadingNewerRef.current && !isLoadingNewer && newerSpinnerHeightRef.current > 0) {
      list.scrollTop = Math.max(0, list.scrollTop - newerSpinnerHeightRef.current);
      newerSpinnerHeightRef.current = 0;
    }

    prevIsLoadingNewerRef.current = isLoadingNewer;
  }, [isLoadingNewer]);

  // IntersectionObserver to trigger loading older messages on scroll up.
  // Uses refs for guards so the observer is created only once (not recreated on every state change).
  React.useEffect(() => {
    const sentinel = sentinelRef.current;
    const list = listRef.current;
    if (!sentinel || !list) return;

    // Use root: null (viewport) because the List element may not be the actual
    // scroll container (ChatMain might scroll instead). With root: list, the sentinel
    // would always be "intersecting" and the observer would fire once then go silent.
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        if (!hasMoreRef.current || isLoadingOlderRef.current) return;
        if (loadInProgressRef.current || scrollToActiveRef.current) return;
        loadInProgressRef.current = true;
        // Save anchor: the first actual message element (skip sentinel + spinners)
        suppressAutoScrollRef.current = true;
        cancelPendingScrollToBottom?.();
        const msgEl = list.querySelector('[data-msg-id]');
        if (msgEl) {
          anchorElRef.current = msgEl;
          anchorTopRef.current = msgEl.getBoundingClientRect().top;
        }
        loadOlderRef.current();
      },
      { root: null, threshold: 0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []); // stable — guards are checked via refs inside callback

  // Restore scroll position after older messages are prepended
  React.useLayoutEffect(() => {
    const prevCount = prevMessageCountRef.current;
    const currentCount = messages.length;
    prevMessageCountRef.current = currentCount;

    const anchor = anchorElRef.current;
    // Only restore scroll when messages were PREPENDED and we have an anchor
    if (currentCount > prevCount && anchor && suppressAutoScrollRef.current) {
      // The anchor element is still in the DOM — scroll so it's at the same screen position
      const currentTop = anchor.getBoundingClientRect().top;
      const drift = currentTop - anchorTopRef.current;
      if (Math.abs(drift) > 1) {
        // Find every scrollable ancestor and adjust the one that moved
        let el: HTMLElement | null = anchor as HTMLElement;
        while (el) {
          if (el.scrollHeight > el.clientHeight && (el.scrollTop > 0 || drift > 0)) {
            el.scrollTop += drift;
            break;
          }
          el = el.parentElement;
        }
      }
      anchorElRef.current = null;
      // Allow observer to fire again for the next page
      loadInProgressRef.current = false;
      // Keep suppressAutoScrollRef true — it will be cleared when a new
      // message arrives or the user sends a message (lastMessageId change).
      // This prevents image loads in older messages from triggering scroll-to-bottom.
    } else if (!anchor) {
      // No anchor means this wasn't a prepend triggered by loadOlderMessages
      loadInProgressRef.current = false;
      suppressAutoScrollRef.current = false;
    }

    // Scroll restoration for APPENDED newer messages (bottom anchor)
    const bottomAnchor = bottomAnchorElRef.current;
    if (currentCount > prevCount && bottomAnchor && loadNewerInProgressRef.current) {
      const currentTop = bottomAnchor.getBoundingClientRect().top;
      const drift = currentTop - bottomAnchorTopRef.current;
      if (Math.abs(drift) > 1) {
        let el: HTMLElement | null = bottomAnchor as HTMLElement;
        while (el) {
          if (el.scrollHeight > el.clientHeight && (el.scrollTop > 0 || drift > 0)) {
            el.scrollTop += drift;
            break;
          }
          el = el.parentElement;
        }
      }
      bottomAnchorElRef.current = null;
      loadNewerInProgressRef.current = false;
    }
  }, [messages]);

  // IntersectionObserver for bottom sentinel — loading newer messages
  React.useEffect(() => {
    const sentinel = bottomSentinelRef.current;
    const list = listRef.current;
    if (!sentinel || !list) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        if (!hasMoreNewerRef.current || isLoadingNewerRef.current) return;
        if (loadNewerInProgressRef.current || scrollToActiveRef.current) return;
        loadNewerInProgressRef.current = true;

        // Save anchor: the LAST message element for scroll restoration after append
        const allMsgEls = list.querySelectorAll('[data-msg-id]');
        const lastMsgEl = allMsgEls[allMsgEls.length - 1];
        if (lastMsgEl) {
          bottomAnchorElRef.current = lastMsgEl;
          bottomAnchorTopRef.current = lastMsgEl.getBoundingClientRect().top;
        }

        loadNewerRef.current?.();
      },
      { root: null, threshold: 0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMoreNewer]); // re-create when hasMoreNewer changes so sentinel appears/disappears

  const [ttsLoadingId, setTtsLoadingId] = React.useState<string | null>(null);
  const [fetchLoadingId, setFetchLoadingId] = React.useState<string | null>(null);
  const [ttsErrorById, setTtsErrorById] = React.useState<Record<string, string | null>>({});

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [, setActiveId] = React.useState<string | null>(null);

  const [playingId, setPlayingId] = React.useState<string | null>(null);
  const [isPaused, setIsPaused] = React.useState(false);

  const [activeProgressPct, setActiveProgressPct] = React.useState(0);
  const [activeElapsedMs, setActiveElapsedMs] = React.useState(0);

  // Refs for high-frequency progress tracking — the RAF tick writes to these
  // every frame, but we only flush to React state at ~12fps to avoid
  // re-rendering the entire conversation list 60 times per second.
  const progressRef = React.useRef({ pct: 0, elapsedMs: 0 });
  const lastFlushRef = React.useRef(0);

  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const audioCacheRef = React.useRef<Map<string, AudioCacheEntry>>(new Map());

  const [durationMsById, setDurationMsById] = React.useState<Record<string, number>>({});
  const durationMsByIdRef = React.useRef<Record<string, number>>({});
  React.useEffect(() => {
    // Merge state values into the ref — do NOT replace the ref object with the
    // state object.  Replacing aliases them so that direct ref mutations
    // (durationMsByIdRef.current[id] = ms) silently mutate React state, which
    // causes the setDurationMsById functional updater guard to see the value
    // and skip the update, preventing re-renders.
    for (const k of Object.keys(durationMsById)) {
      durationMsByIdRef.current[k] = durationMsById[k];
    }
  }, [durationMsById]);

  const urlToDurationMsRef = React.useRef<Map<string, number>>(new Map());

  const messageElRef = React.useRef<Record<string, HTMLDivElement | null>>({});


  const glowStyle = React.useMemo(
    () => (
      <style>
        {`
          @keyframes ttsGlow {
            0%   { box-shadow: 0 0 0 rgba(94,132,237,0.00), 0 0 0 rgba(94,132,237,0.00); transform: translateZ(0); }
            50%  { box-shadow: 0 0 22px rgba(94,132,237,0.38), 0 0 60px rgba(94,132,237,0.18); transform: translateZ(0); }
            100% { box-shadow: 0 0 0 rgba(94,132,237,0.00), 0 0 0 rgba(94,132,237,0.00); transform: translateZ(0); }
          }
          @keyframes searchHighlight {
            0%   { background-color: rgba(94,132,237,0.0); }
            8%   { background-color: rgba(94,132,237,0.22); }
            30%  { background-color: rgba(94,132,237,0.18); }
            100% { background-color: rgba(94,132,237,0.0); }
          }
          .search-highlight {
            animation: searchHighlight 2.2s ease-out;
            border-radius: 12px;
          }
        `}
      </style>
    ),
    []
  );

  React.useEffect(() => {
    return () => {
      try {
        for (const entry of audioCacheRef.current.values()) {
          if (entry.revokeOnCleanup) {
            try {
              URL.revokeObjectURL(entry.url);
            } catch {
              // ignore
            }
          }
        }
        audioCacheRef.current.clear();
      } catch {
        // ignore
      }

      try {
        const a = audioRef.current;
        if (a) {
          a.pause();
          a.src = '';
        }
      } catch {
        // ignore
      }
    };
  }, []);

  React.useEffect(() => {
    const a = audioRef.current;
    if (!a) return;

    let raf = 0;

    const FLUSH_INTERVAL = 80; // ms — flush React state ~12fps
    const tick = () => {
      try {
        const tSec = Number(a.currentTime);
        const liveDSec = Number(a.duration);

        const knownMs = durationMsByIdRef.current[playingId ?? ''] ?? 0;
        let dSec = knownMs > 0 ? knownMs / 1000 : liveDSec;

        if (knownMs === 0 && Number.isFinite(liveDSec) && liveDSec > 0) {
          const liveMs = Math.round(liveDSec * 1000);
          durationMsByIdRef.current[playingId ?? ''] = liveMs;
          setDurationMsById((prev) => {
            if ((prev[playingId ?? ''] ?? 0) > 0) return prev;
            return { ...prev, [playingId ?? '']: liveMs };
          });
          dSec = liveDSec;
        }

        const pct =
          Number.isFinite(dSec) && dSec > 0 && Number.isFinite(tSec)
            ? Math.max(0, Math.min(1, tSec / dSec))
            : 0;
        const elapsed = Number.isFinite(tSec) && tSec >= 0 ? Math.round(tSec * 1000) : 0;

        // Store latest values in ref (every frame)
        progressRef.current.pct = pct;
        progressRef.current.elapsedMs = elapsed;

        // Flush to React state at a throttled rate to avoid re-rendering
        // the entire conversation list 60x/sec which causes React concurrent
        // mode to drop intermediate renders (seekbar appears stuck).
        const now = performance.now();
        if (now - lastFlushRef.current >= FLUSH_INTERVAL) {
          lastFlushRef.current = now;
          setActiveProgressPct(pct);
          setActiveElapsedMs(elapsed);
        }
      } catch {
        setActiveProgressPct(0);
      }
      raf = requestAnimationFrame(tick);
    };

    if (playingId && !isPaused) {
      lastFlushRef.current = 0; // Force immediate first flush
      raf = requestAnimationFrame(tick);
      return () => {
        cancelAnimationFrame(raf);
        // Flush final value so seekbar shows correct position when paused
        setActiveProgressPct(progressRef.current.pct);
        setActiveElapsedMs(progressRef.current.elapsedMs);
      };
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [playingId, isPaused]);

  const stopPlayback = React.useCallback(() => {
    try {
      const a = audioRef.current;
      if (a) {
        a.pause();
        a.currentTime = 0;
      }
    } catch {
      // ignore
    } finally {
      setPlayingId(null);
      setIsPaused(false);
      setActiveProgressPct(0);
      setActiveElapsedMs(0);
      progressRef.current.pct = 0;
      progressRef.current.elapsedMs = 0;
    }
  }, []);

  const pausePlayback = React.useCallback(() => {
    try {
      const a = audioRef.current;
      if (a) a.pause();
    } catch {
      // ignore
    } finally {
      setIsPaused(true);
    }
  }, []);

  const resumePlayback = React.useCallback(() => {
    try {
      const a = audioRef.current;
      if (a) {
        a.play?.().catch(() => {
          // ignore
        });
      }
    } catch {
      // ignore
    } finally {
      setIsPaused(false);
    }
  }, []);

  const seekPlaybackToPct = React.useCallback(
    (pct: number) => {
      const a = audioRef.current;
      if (!a) return;

      // Prefer stored decoded duration over live a.duration (Chrome WebM
      // can report wrong finite durations mid-playback).
      const knownMs = durationMsByIdRef.current[playingId ?? ''] ?? 0;
      let d = knownMs > 0 ? knownMs / 1000 : Number(a.duration);
      if (!Number.isFinite(d) || d <= 0) return;

      const clamped = Math.max(0, Math.min(1, pct));
      try {
        a.currentTime = clamped * d;

        // Immediately reflect seek in UI even when paused (no RAF ticking)
        progressRef.current.pct = clamped;
        progressRef.current.elapsedMs = Math.round(clamped * d * 1000);
        setActiveProgressPct(clamped);
        setActiveElapsedMs(Math.round(clamped * d * 1000));
      } catch {
        // ignore
      }
    },
    [playingId]
  );

  const ensureDurationMsForUrl = React.useCallback(async (url: string): Promise<number> => {
    const u = String(url || '').trim();
    if (!u) return 0;

    const cached = urlToDurationMsRef.current.get(u);
    if (typeof cached === 'number' && Number.isFinite(cached) && cached > 0) return cached;

    return await new Promise<number>((resolve) => {
      let resolved = false;

      try {
        const probe = new Audio();
        // Need 'auto' (not 'metadata') so Chrome buffers enough for the
        // seek-to-end trick that forces WebM duration computation.
        probe.preload = 'auto';

        const cleanup = () => {
          try {
            probe.onloadedmetadata = null;
            probe.ondurationchange = null;
            probe.onerror = null;
            probe.src = '';
          } catch {
            // ignore
          }
        };

        const done = (ms: number) => {
          if (resolved) return;
          resolved = true;
          if (ms > 0) urlToDurationMsRef.current.set(u, ms);
          cleanup();
          resolve(ms);
        };

        probe.onloadedmetadata = () => {
          const sec = Number(probe.duration);
          if (Number.isFinite(sec) && sec > 0) {
            done(Math.round(sec * 1000));
            return;
          }
          // Chrome WebM workaround: WebM files from MediaRecorder lack a
          // duration header so duration stays Infinity until fully buffered.
          // Seeking to a huge value forces Chrome to compute the real duration.
          try { probe.currentTime = 1e99; } catch { /* ignore */ }
        };

        // Fires when duration transitions from Infinity → finite after seek trick
        probe.ondurationchange = () => {
          const sec = Number(probe.duration);
          if (Number.isFinite(sec) && sec > 0) {
            done(Math.round(sec * 1000));
          }
        };

        probe.onerror = () => done(0);

        // Safety timeout — don't block forever
        setTimeout(() => done(0), 8000);

        probe.src = u;
      } catch {
        if (!resolved) {
          resolved = true;
          resolve(0);
        }
      }
    });
  }, []);

  const ensureDurationMsForMessage = React.useCallback(
    async (messageId: string, url: string) => {
      const existing = durationMsByIdRef.current[messageId];
      if (typeof existing === 'number' && existing > 0) return;

      const ms = await ensureDurationMsForUrl(url);
      if (!ms) return;

      setDurationMsById((prev) => {
        if (prev[messageId] && prev[messageId] > 0) return prev;
        return { ...prev, [messageId]: ms };
      });
    },
    [ensureDurationMsForUrl]
  );

  const playFromCache = React.useCallback(
    (messageId: string) => {
      const entry = audioCacheRef.current.get(messageId);
      if (!entry) return false;

      const a = audioRef.current;
      if (!a) return false;

      try {
        if (playingId && playingId !== messageId) {
          stopPlayback();
        }

        a.onended = () => {
          setPlayingId(null);
          setIsPaused(false);
          setActiveProgressPct(0);
          setActiveElapsedMs(0);
          progressRef.current.pct = 0;
          progressRef.current.elapsedMs = 0;
        };
        a.onerror = () => {
          setPlayingId(null);
          setIsPaused(false);
          setActiveProgressPct(0);
          setActiveElapsedMs(0);
          progressRef.current.pct = 0;
          progressRef.current.elapsedMs = 0;
        };

        const persistDuration = () => {
          const sec = Number(a.duration);
          const ms = Number.isFinite(sec) && sec > 0 ? Math.round(sec * 1000) : 0;
          if (ms > 0) {
            durationMsByIdRef.current[messageId] = ms;
            urlToDurationMsRef.current.set(entry.url, ms);
            setDurationMsById((prev) => {
              const cur = prev[messageId] ?? 0;
              if (cur && cur > 0) return prev;
              return { ...prev, [messageId]: ms };
            });
          }
        };

        a.onloadedmetadata = persistDuration;
        a.ondurationchange = persistDuration;

        // Guard: if this exact message is already playing the same URL, skip restart
        if (a.src === entry.url && !a.paused && playingId === messageId) {
          return true;
        }

        a.src = entry.url;
        a.currentTime = 0;

        progressRef.current.pct = 0;
        progressRef.current.elapsedMs = 0;
        setActiveProgressPct(0);
        setActiveElapsedMs(0);
        setActiveId(messageId);
        setPlayingId(messageId);
        setIsPaused(false);

        a.play?.().catch(() => {
          // ignore
        });

        return true;
      } catch {
        return false;
      }
    },
    [playingId, stopPlayback]
  );

  const playServerAudioUrl = React.useCallback(
    async (messageId: string, url: string) => {
      const u = String(url || '').trim();
      if (!u) return;

      // Already cached as blob — play immediately
      if (audioCacheRef.current.has(messageId)) {
        stopPlayback();
        playFromCache(messageId);
        return;
      }

      // Fetch as blob so we can decode exact duration upfront. Signed storage
      // URLs often stream without Content-Length and MediaRecorder WebM files
      // lack a duration header, so a.duration stays Infinity until the file is
      // nearly fully buffered. decodeAudioData gives us the real value before
      // playback starts so the seeker shows elapsed / total from frame 1.
      setFetchLoadingId(messageId);
      try {
        const MAX_RETRIES = 3;
        for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
          try {
            const res = await fetch(u);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const blobUrl = URL.createObjectURL(blob);

            const ms = await decodeDurationMs(blob);
            if (ms > 0) {
              durationMsByIdRef.current[messageId] = ms;
              setDurationMsById((prev) => {
                if ((prev[messageId] ?? 0) > 0) return prev;
                return { ...prev, [messageId]: ms };
              });
            }

            audioCacheRef.current.set(messageId, {
              url: blobUrl,
              createdAt: Date.now(),
              revokeOnCleanup: true,
            });

            stopPlayback();
            playFromCache(messageId);
            return;
          } catch {
            if (attempt < MAX_RETRIES - 1) {
              await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
              continue;
            }
            // Final fallback: stream directly from the URL
            try {
              audioCacheRef.current.set(messageId, {
                url: u,
                createdAt: Date.now(),
                revokeOnCleanup: false,
              });
              await ensureDurationMsForMessage(messageId, u);
              stopPlayback();
              playFromCache(messageId);
            } catch {
              // Exhausted all retries — silently give up
            }
          }
        }
      } finally {
        setFetchLoadingId(null);
      }
    },
    [ensureDurationMsForMessage, playFromCache, stopPlayback]
  );

  const playUserAudioUrl = React.useCallback(
    async (messageId: string, url: string) => {
      const u = String(url || '').trim();
      if (!u) return;

      // Already cached as blob — play immediately
      if (audioCacheRef.current.has(messageId)) {
        stopPlayback();
        playFromCache(messageId);
        return;
      }

      const revokeOnCleanup = u.startsWith('blob:');

      // Decode exact duration BEFORE starting playback — needed for WebM/Opus
      // recordings which report Infinity via HTMLAudioElement.duration until near EOF.
      // Without this, tick() computes pct = currentTime / Infinity = 0 and the
      // progress bar stays stuck at 0% while audio plays.
      setFetchLoadingId(messageId);
      try {
        const existingMs = durationMsByIdRef.current[messageId] ?? 0;
        if (existingMs <= 0) {
          const MAX_RETRIES = 3;
          let fetched = false;
          for (let attempt = 0; attempt < MAX_RETRIES && !fetched; attempt++) {
            try {
              const res = await fetch(u);
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              const blob = await res.blob();
              const blobUrl = URL.createObjectURL(blob);
              const ms = await decodeDurationMs(blob);
              if (ms > 0) {
                durationMsByIdRef.current[messageId] = ms;
                urlToDurationMsRef.current.set(u, ms);
                setDurationMsById((prev) => {
                  if ((prev[messageId] ?? 0) > 0) return prev;
                  return { ...prev, [messageId]: ms };
                });
              }

              audioCacheRef.current.set(messageId, {
                url: blobUrl,
                createdAt: Date.now(),
                revokeOnCleanup: true,
              });
              fetched = true;
            } catch {
              if (attempt < MAX_RETRIES - 1) {
                await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
                continue;
              }
              // Final fallback: use the original URL directly
              audioCacheRef.current.set(messageId, {
                url: u,
                createdAt: Date.now(),
                revokeOnCleanup,
              });
              await ensureDurationMsForMessage(messageId, u);
            }
          }
        } else {
          audioCacheRef.current.set(messageId, {
            url: u,
            createdAt: Date.now(),
            revokeOnCleanup,
          });
        }

        stopPlayback();
        playFromCache(messageId);
      } catch {
        // Exhausted all retries — silently give up
      } finally {
        setFetchLoadingId(null);
      }
    },
    [ensureDurationMsForMessage, playFromCache, stopPlayback]
  );

  const synthesizeIfNeededAndPlay = React.useCallback(
    async (messageId: string, text: string) => {
      const t = String(text ?? '').trim();
      if (!t) return;

      const cached = audioCacheRef.current.get(messageId);
      if (cached) {
        playFromCache(messageId);
        return;
      }

      if (ttsLoadingId === messageId) return;

      try {
        setTtsErrorById((prev) => ({ ...prev, [messageId]: null }));
        setTtsLoadingId(messageId);
        setActiveId(messageId);

        stopPlayback();

        // Call backend TTS proxy — keeps ElevenLabs key server-side
        const ttsRes = await api.post<{ audio_url: string }>('/tts', {
          text: t,
          folder: 'tts',
          filename: `msg-${messageId}`,
        });

        const audioUrl = ttsRes.data.audio_url;
        if (!audioUrl) throw new Error('No audio URL returned from TTS');

        // Fetch the stored audio file and create a blob URL for playback
        const audioRes = await fetch(audioUrl);
        if (!audioRes.ok) throw new Error(`Failed to fetch audio: HTTP ${audioRes.status}`);
        const blob = await audioRes.blob();
        const url = URL.createObjectURL(blob);

        audioCacheRef.current.set(messageId, {
          url,
          createdAt: Date.now(),
          revokeOnCleanup: true,
        });

        void ensureDurationMsForMessage(messageId, url);

        playFromCache(messageId);
      } catch (err: any) {
        setTtsErrorById((prev) => ({
          ...prev,
          [messageId]: err?.message ?? 'Something went wrong while generating speech.',
        }));
        setPlayingId(null);
        setIsPaused(false);
        setActiveProgressPct(0);
      } finally {
        setTtsLoadingId(null);
      }
    },
    [ensureDurationMsForMessage, playFromCache, stopPlayback, ttsLoadingId]
  );

  // ✅ Single source of truth: all scroll-to-bottom goes through the parent's scrollToBottom()
  // which scrolls to the chatEndRef anchor at the very bottom of ChatWrapper.

  // Track whether user is near bottom (used to auto-scroll on content growth)
  const isNearBottomRef = React.useRef(true);

  React.useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const onScroll = () => {
      const threshold = 150;
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      isNearBottomRef.current = distanceFromBottom <= threshold;
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Scroll to bottom when a new message is APPENDED (not when older messages are prepended)
  const lastMessageIdRef = React.useRef<string | null>(null);

  React.useLayoutEffect(() => {
    if (!scrollToBottom || messages.length === 0) return;
    if (hasMoreNewer) return;
    // Don't fight with scroll-to-message positioning
    if (scrollToMessageId || scrollToActiveRef.current) return;

    const lastMsg = messages[messages.length - 1];
    const lastId = lastMsg?.ui_id ?? lastMsg?.id ?? null;
    if (!lastId || lastId === lastMessageIdRef.current) return;

    lastMessageIdRef.current = lastId;

    // A genuinely new message arrived — clear all flags and scroll to bottom
    loadInProgressRef.current = false;
    suppressAutoScrollRef.current = false;
    unsuppressScrollToBottom?.();
    isNearBottomRef.current = true;
    scrollToBottom();
  }, [messages, scrollToBottom, hasMoreNewer, scrollToMessageId, unsuppressScrollToBottom]);

  // Scroll to bottom on initial mount
  const mountScrollDoneRef = React.useRef(false);

  React.useEffect(() => {
    if (mountScrollDoneRef.current || !scrollToBottom) return;
    if (messages.length === 0) return;

    mountScrollDoneRef.current = true;
    if (scrollToMessageId) return;

    isNearBottomRef.current = true;
    scrollToBottom();
  }, [messages, scrollToMessageId, scrollToBottom]);

  // NOTE: MutationObserver/ResizeObserver for auto-scroll on content growth removed.
  // It caused scroll-to-bottom jumps when loading older messages and when images loaded.
  // Scroll-to-bottom on new message (lastMessageId change) and mount are sufficient.

  // Smooth-scroll to bottom when a transcription result arrives (content was empty → now filled)
  const transcribingIdsRef = React.useRef<Set<string>>(new Set());

  const TRANSCRIPTION_TIMEOUT_MS = 45_000; // 45 seconds

  // Check if a message's transcription has timed out based on created_at
  const isTranscriptionTimedOut = React.useCallback((m: UiMessage): boolean => {
    if (!m.created_at) return false;
    const created = new Date(m.created_at).getTime();
    return Date.now() - created > TRANSCRIPTION_TIMEOUT_MS;
  }, []);

  // Force a re-render when pending transcriptions should time out
  const [, forceTranscriptionCheck] = React.useReducer((x: number) => x + 1, 0);

  React.useEffect(() => {
    const nextTranscribing = new Set<string>();
    let soonestTimeoutMs = Infinity;

    for (const m of messages) {
      const isTranscribing = m.ui_status === 'sent' && m.has_audio && !String(m.content ?? '').trim()
        && !m.ui_transcription_failed && !isTranscriptionTimedOut(m);

      if (isTranscribing) {
        nextTranscribing.add(m.ui_id);
        // Calculate when this message will time out
        const created = new Date(m.created_at).getTime();
        const remaining = TRANSCRIPTION_TIMEOUT_MS - (Date.now() - created);
        if (remaining > 0) soonestTimeoutMs = Math.min(soonestTimeoutMs, remaining);
      } else if (transcribingIdsRef.current.has(m.ui_id) && String(m.content ?? '').trim()) {
        // This message was transcribing and now has content — scroll if near bottom
        if (isNearBottomRef.current) {
          const list = listRef.current;
          if (list?.scrollTo) {
            list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' });
          }
        }
      }
    }

    transcribingIdsRef.current = nextTranscribing;

    // Schedule a re-render when the soonest transcribing message times out
    if (soonestTimeoutMs < Infinity) {
      const timer = setTimeout(forceTranscriptionCheck, soonestTimeoutMs + 200);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [messages, isTranscriptionTimedOut]);

  // ── Auto-play: triggered from notification toast "Listen" button ──
  const autoPlayHandledRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!autoPlayMessageId || autoPlayMessageId === autoPlayHandledRef.current) return;
    if (messages.length === 0) return;

    autoPlayHandledRef.current = autoPlayMessageId;

    // Find the target message
    const target = messages.find(
      (m) => String(m.ui_id ?? m.id ?? '') === autoPlayMessageId,
    );
    if (!target) {
      onAutoPlayHandled?.();
      return;
    }

    const msgId = String((target as any).ui_id ?? (target as any).id ?? '');
    const audioSrc =
      ((target as any).ui_audio_url as string | null) ??
      ((target as any).audio_url as string | null) ??
      null;
    const isMine = !!profile?.id && (target as any).sender_id === profile.id;

    // Small delay to ensure the conversation is scrolled and DOM is settled
    const timer = setTimeout(() => {
      if (audioSrc) {
        if (isMine) {
          playUserAudioUrl(msgId, audioSrc);
        } else {
          playServerAudioUrl(msgId, audioSrc);
        }
      } else if (!isMine) {
        // Admin message with no audio — synthesize via TTS
        synthesizeIfNeededAndPlay(msgId, String((target as any).content ?? ''));
      }

      // Stay scrolled at the bottom
      scrollToBottom?.();
      onAutoPlayHandled?.();
    }, 300);

    return () => clearTimeout(timer);
  }, [autoPlayMessageId, messages, profile?.id, playServerAudioUrl, playUserAudioUrl, synthesizeIfNeededAndPlay, onAutoPlayHandled]);

  // ── Scroll-to-message: position target near top before browser paints ──
  const scrollHandledRef = React.useRef<string | null>(null);

  React.useLayoutEffect(() => {
    if (!scrollToMessageId || scrollToMessageId === scrollHandledRef.current) return;

    // Activate guard — blocks observers, auto-scroll, and loadMessages
    scrollToActiveRef.current = true;

    if (messages.length === 0) return;

    // Find the target element
    let targetEl: HTMLDivElement | null = messageElRef.current[scrollToMessageId] ?? null;
    if (!targetEl) {
      const target = messages.find(
        (m) => String(m.server_id ?? '') === scrollToMessageId || String(m.id) === scrollToMessageId,
      );
      if (target) {
        targetEl = messageElRef.current[String(target.ui_id ?? target.id ?? '')] ?? null;
      }
    }
    if (!targetEl) return;

    scrollHandledRef.current = scrollToMessageId;
    cancelPendingScrollToBottom?.();

    // If we have a search query, find and wrap the matching text with a <mark>
    let markEl: HTMLElement | null = null;
    if (searchHighlightQuery?.trim()) {
      const query = searchHighlightQuery.trim();
      const walker = document.createTreeWalker(targetEl, NodeFilter.SHOW_TEXT);
      const queryLower = query.toLowerCase();
      let node: Text | null;
      while ((node = walker.nextNode() as Text | null)) {
        const idx = node.textContent?.toLowerCase().indexOf(queryLower) ?? -1;
        if (idx === -1) continue;
        // Split the text node and wrap the match in a <mark>
        const before = node.splitText(idx);
        const match = before.splitText(query.length);
        void match; // remaining text after match
        const mark = document.createElement('mark');
        mark.style.backgroundColor = 'rgba(255, 220, 50, 0.65)';
        mark.style.borderRadius = '3px';
        mark.style.padding = '1px 2px';
        mark.style.transition = 'background-color 0.5s ease-out';
        before.parentNode!.replaceChild(mark, before);
        mark.appendChild(document.createTextNode(before.textContent ?? ''));
        markEl = mark;
        break;
      }
    }

    // Center on the highlighted <mark> if found, otherwise center the whole message
    const scrollTarget = markEl ?? targetEl;
    const list = targetEl.closest('[aria-label="Conversation"]') as HTMLElement | null;
    if (list) {
      const listRect = list.getBoundingClientRect();
      const elRect = scrollTarget.getBoundingClientRect();
      list.scrollTop += elRect.top - listRect.top - (listRect.height / 2) + (elRect.height / 2);
    }

    // Defer state cleanup so it doesn't cause a sync re-render before paint
    window.setTimeout(() => {
      onScrollHandled?.();
      // Re-enable observers and auto-scroll after positioning is stable
      scrollToActiveRef.current = false;
      unsuppressScrollToBottom?.();
    }, 0);

    if (markEl) {
      // Fade out the yellow highlight after 2 seconds, then remove the <mark>
      window.setTimeout(() => {
        markEl!.style.backgroundColor = 'transparent';
        window.setTimeout(() => {
          // Unwrap the <mark> — replace it with its text content
          const text = document.createTextNode(markEl!.textContent ?? '');
          markEl!.parentNode?.replaceChild(text, markEl!);
          text.parentNode?.normalize();
        }, 500);
      }, 2000);
    } else {
      // No search query — fall back to highlighting the whole message
      window.setTimeout(() => {
        targetEl!.classList.add('search-highlight');
        window.setTimeout(() => targetEl!.classList.remove('search-highlight'), 2200);
      }, 0);
    }
  }, [scrollToMessageId, messages, onScrollHandled, cancelPendingScrollToBottom, unsuppressScrollToBottom, searchHighlightQuery]);

  return (
    <>
      {glowStyle}

      <Wrap>
        <ListWrap>
          <List ref={listRef} aria-label="Conversation">
            {/* Sentinel + spinner for infinite scroll */}
            <div ref={sentinelRef} style={{ height: 1, flexShrink: 0 }} />
            {isLoadingOlder && (
              <div
                ref={olderSpinnerRef}
                style={{ display: 'flex', justifyContent: 'center', padding: '12px 0' }}
              >
                <UniversalSpinner />
              </div>
            )}
            {isLoading && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
                <UniversalSpinner />
              </div>
            )}
            {messages.map((m) => {
              const isMine = !!profile?.id && m.sender_id === profile.id;
              const isAdmin = !isMine;

              const dateTimeLabel = formatMessageDateTime((m as any).updated_at ?? m.created_at);

              if (!isAdmin) {
                const initials =
                  `${String(profile?.first_name ?? '')
                    .trim()
                    .charAt(0)}${String(profile?.last_name ?? '')
                    .trim()
                    .charAt(0)}`.trim() ||
                  (profile?.nickname?.[0]?.toUpperCase() ?? '?');

                const playableUserAudioSrc =
                  (m.ui_audio_url as string | null) ?? (m.audio_url as string | null) ?? null;
                const hasUserAudio = !!String(playableUserAudioSrc ?? '').trim();

                const userMsgId = String(m.ui_id ?? m.id ?? '');
                const userIsThisPlaying = playingId === userMsgId && !isPaused;
                const userIsThisPaused = playingId === userMsgId && isPaused;
                const userHasCachedAudio = audioCacheRef.current.has(userMsgId);

                const userDurationMs = durationMsById[userMsgId] ?? 0;
                const userProgressPct =
                  playingId === userMsgId
                    ? activeProgressPct
                    : userIsThisPaused
                      ? activeProgressPct
                      : 0;

                const startUser = () => {
                  const u = String(playableUserAudioSrc ?? '').trim();
                  if (!u) return;
                  playUserAudioUrl(userMsgId, u);
                };

                const toggleUserPause = () => {
                  if (playingId !== userMsgId) return;
                  if (isPaused) resumePlayback();
                  else pausePlayback();
                };

                const seekUser = (pct: number) => {
                  if (playingId !== userMsgId) return;
                  seekPlaybackToPct(pct);
                };

                /* ── USER MESSAGE: 2-column layout (content | avatar) ── */
                return (
                  <Row key={m.ui_id} isMine data-msg-id={m.ui_id} ref={(el: HTMLDivElement | null) => { messageElRef.current[userMsgId] = el; }}>
                    <ContentCol style={{ paddingLeft: 42 }}>
                      {/* Row 1: Listen button (only if audio attached) */}
                      {hasUserAudio && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <ConversationListenButton
                            onStart={startUser}
                            onTogglePause={toggleUserPause}
                            onSeek={seekUser}
                            loading={fetchLoadingId === userMsgId}
                            playing={userIsThisPlaying}
                            paused={userIsThisPaused}
                            hasAudio={userHasCachedAudio || hasUserAudio}
                            durationMs={userDurationMs}
                            progressPct={userProgressPct}
                            elapsedMs={playingId === userMsgId ? activeElapsedMs : 0}
                            spinner={<UniversalSpinner />}
                            title={userIsThisPlaying ? 'Pause' : 'Listen'}
                            direction="rtl"
                          />
                          {m.ui_status === 'pending' && m.ui_upload_progress != null && (
                            <UploadProgressPill>{m.ui_upload_progress}%</UploadProgressPill>
                          )}
                        </div>
                      )}

                      {/* Row 2: Message text + files */}
                      <MineConversationWrapper>
                        <MineText>
                          {String(m.content ?? '').trim() ? (
                            <span>{linkify(String(m.content ?? ''))}</span>
                          ) : m.ui_status === 'sent' && m.has_audio && !isTranscriptionTimedOut(m) && !m.ui_transcription_failed ? (
                            <TranscribingIndicator>
                              <UniversalSpinner />
                              <span>Transcribing...</span>
                            </TranscribingIndicator>
                          ) : null}

                          {m.ui_status === 'pending' && m.ui_local_file_previews?.length ? (
                            <div>
                              {m.ui_local_file_previews.map((f, i) => (
                                <LocalFilePreviewCard
                                  key={`local-${i}`}
                                  file={f}
                                  progress={m.ui_upload_progress}
                                  {...(m.ui_upload_progress != null && cancelUpload ? { onCancel: () => cancelUpload(m.ui_id) } : {})}
                                />
                              ))}
                            </div>
                          ) : null}

                          {!!m.files?.length && (
                            <div>
                              {m.files.map((f) => (
                                <FileAttachment key={f.id ?? f.name} file={f} />
                              ))}
                            </div>
                          )}

                          {/* Row 3: Date + status */}
                          <DateAndLabelRow>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              {dateTimeLabel}
                              {m.ui_status === 'pending' ? (
                                <span
                                  style={{ display: 'inline-flex', alignItems: 'center' }}
                                  aria-label="Sending"
                                >
                                  <UniversalSpinner />
                                </span>
                              ) : m.ui_status === 'failed' ? (
                                <RetryTap onClick={() => retryMessage?.(m.ui_id)}>
                                  Failed · Tap to retry
                                </RetryTap>
                              ) : m.ui_status === 'sent' && m.sent_via_sms ? (
                                <SentCheck>• ✓</SentCheck>
                              ) : m.ui_status === 'sent' ? (
                                <SentCheck>✓</SentCheck>
                              ) : null}
                            </span>
                            {m?.sent_via_sms && (
                              <SentViaSmsLabel>
                                Sent via SMS
                              </SentViaSmsLabel>
                            )}
                          </DateAndLabelRow>
                        </MineText>
                      </MineConversationWrapper>
                    </ContentCol>

                    <AvatarCol>
                      <AppAvatar
                        userId={String(profile?.id ?? '')}
                        version={(profile as any)?.avatar_version ?? (profile as any)?.updated_at ?? null}
                        hasAvatar={!!profile?.avatar_url}
                        alt="You"
                        initials={initials}
                        width={34}
                        height={34}
                        imgStyle={{ borderRadius: '999px', objectFit: 'cover' }}
                        fallbackStyle={{ borderRadius: '999px' }}
                      />
                    </AvatarCol>
                  </Row>
                );
              }

              /* ── ADMIN MESSAGE: 2-column layout (avatar | content) ── */
              const msgId = String(m.ui_id ?? m.id ?? '');
              const isThisFetching = fetchLoadingId === msgId;

              const playableAdminAudioSrc =
                (m.ui_audio_url as string | null) ?? (m.audio_url as string | null) ?? null;
              const hasServerAudio = !!String(playableAdminAudioSrc ?? '').trim();

              const isThisPlaying = playingId === msgId && !isPaused;
              const isThisPaused = playingId === msgId && isPaused;
              const hasCachedAudio = audioCacheRef.current.has(msgId);
              const isThisLoading = isThisFetching;

              const adminDurationMs = durationMsById[msgId] ?? 0;
              const adminProgressPct =
                playingId === msgId ? activeProgressPct : isThisPaused ? activeProgressPct : 0;

              const err = ttsErrorById[msgId];

              const startAdmin = () => {
                const u = String(playableAdminAudioSrc ?? '').trim();
                if (u) {
                  playServerAudioUrl(msgId, u);
                  return;
                }
              };

              const toggleAdminPause = () => {
                if (playingId !== msgId) return;
                if (isPaused) resumePlayback();
                else pausePlayback();
              };

              const seekAdmin = (pct: number) => {
                if (playingId !== msgId) return;
                seekPlaybackToPct(pct);
              };

              return (
                <Row key={m.ui_id} isMine={false} data-msg-id={m.ui_id} ref={(el: HTMLDivElement | null) => { messageElRef.current[msgId] = el; }}>
                  <AvatarCol>
                    <AppAvatar
                      userId={m.sender_id}
                      alt="Admin"
                      initials="A"
                      fallbackSrc="/chris.jpg"
                      width={34}
                      height={34}
                      imgStyle={{ borderRadius: '999px', objectFit: 'cover' }}
                      fallbackStyle={{ borderRadius: '999px' }}
                    />
                  </AvatarCol>

                  <ContentCol>
                    {/* Row 1: Listen button (only if message has audio) */}
                    {hasServerAudio && (
                      <ConversationListenButton
                        onStart={startAdmin}
                        onTogglePause={toggleAdminPause}
                        onSeek={seekAdmin}
                        loading={isThisLoading}
                        playing={isThisPlaying}
                        paused={isThisPaused}
                        hasAudio={hasCachedAudio || hasServerAudio}
                        durationMs={adminDurationMs}
                        progressPct={adminProgressPct}
                        elapsedMs={playingId === msgId ? activeElapsedMs : 0}
                        spinner={<UniversalSpinner />}
                        title={isThisLoading ? 'Loading...' : isThisPlaying ? 'Pause' : 'Listen'}
                        direction="ltr"
                      />
                    )}

                    {/* Row 2: Message text + files */}
                    <AdminContainer>
                      <AdminText>
                        {linkify(String(m.content ?? ''))}

                        {!!m.files?.length && (
                          <div>
                            {m.files.map((f) => (
                              <FileAttachment key={f.id ?? f.name} file={f} />
                            ))}
                          </div>
                        )}
                      </AdminText>
                    </AdminContainer>

                    {/* Row 3: Date */}
                    <AdminFooter>
                      <DateRow>{dateTimeLabel}</DateRow>
                    </AdminFooter>

                    {err ? (
                      <div
                        role="alert"
                        style={{
                          fontSize: 12,
                          color: '#ff6b6b',
                          textAlign: 'center',
                          width: '100%',
                        }}
                      >
                        {err}
                      </div>
                    ) : null}
                  </ContentCol>
                </Row>
              );
            })}

            {/* Bottom sentinel + spinner for forward pagination */}
            {hasMoreNewer && (
              <div ref={bottomSentinelRef} style={{ height: 1, flexShrink: 0 }} />
            )}
            {isLoadingNewer && (
              <div
                ref={newerSpinnerRef}
                style={{ display: 'flex', justifyContent: 'center', padding: '12px 0' }}
              >
                <UniversalSpinner />
              </div>
            )}
          </List>
        </ListWrap>
      </Wrap>

      <audio ref={audioRef} preload="auto" style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }} />
    </>
  );
};
